import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { DASHBOARD_MODES } from './context/DashboardContext';
import { ErrorBoundary } from '../../shared/components/ErrorBoundary';

/**
 * Initialize the Unified Dashboard React app
 * @param {string|HTMLElement} container - Container element or selector
 * @param {Object} props - Props to pass to App
 * @param {string} props.mode - Dashboard mode: 'kid' or 'guardian'
 * @param {string} props.locale - Locale for i18n
 * @param {string} props.flashNotice - Flash notice message
 * @param {string} props.flashAlert - Flash alert message
 */
export const initUnifiedDashboard = (container, props = {}) => {
  const element = typeof container === 'string'
    ? document.querySelector(container)
    : container;

  if (!element) {
    console.error('Unified Dashboard: Container element not found');
    return;
  }

  const root = ReactDOM.createRoot(element);
  root.render(
    <React.StrictMode>
      <ErrorBoundary name={`unified-dashboard:${props.mode || 'kid'}`}>
        <App {...props} />
      </ErrorBoundary>
    </React.StrictMode>
  );

  return root;
};

/**
 * Initialize kid dashboard from a container element
 */
const initKidDashboardContainers = () => {
  const containers = document.querySelectorAll('[data-kid-dashboard]');
  containers.forEach(container => {
    // Skip if already initialized
    if (container.dataset.dashboardInitialized) return;
    container.dataset.dashboardInitialized = 'true';

    const props = {
      mode: DASHBOARD_MODES.KID,
      locale: container.dataset.locale || document.documentElement.lang || 'vi'
    };
    initUnifiedDashboard(container, props);
  });
};

/**
 * Initialize guardian dashboard from a container element
 */
const initGuardianDashboardContainers = () => {
  const containers = document.querySelectorAll('[data-guardian-dashboard]');
  containers.forEach(container => {
    // Skip if already initialized
    if (container.dataset.dashboardInitialized) return;
    container.dataset.dashboardInitialized = 'true';

    // Parse onboarding data if present
    let onboarding = null;
    try {
      if (container.dataset.onboarding) {
        onboarding = JSON.parse(container.dataset.onboarding);
      }
    } catch (e) {
      console.error('Failed to parse onboarding data:', e);
    }

    const props = {
      mode: DASHBOARD_MODES.GUARDIAN,
      locale: container.dataset.locale || document.documentElement.lang || 'vi',
      railsEnv: container.dataset.railsEnv || 'production',
      flashNotice: container.dataset.flashNotice || '',
      flashAlert: container.dataset.flashAlert || '',
      onboarding: onboarding
    };
    initUnifiedDashboard(container, props);
  });
};

/**
 * Initialize all dashboard containers
 */
const initAllDashboards = () => {
  initKidDashboardContainers();
  initGuardianDashboardContainers();
};

// Auto-initialize if data attributes are present
// Handle both initial page load and Turbo navigation
if (typeof document !== 'undefined') {
  // If DOM is already loaded, initialize immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllDashboards);
  } else {
    initAllDashboards();
  }

  // Also listen for Turbo navigation events
  document.addEventListener('turbo:load', initAllDashboards);
  document.addEventListener('turbo:frame-load', initAllDashboards);
}

export { App } from './App';
export { DASHBOARD_MODES } from './context/DashboardContext';
export default initUnifiedDashboard;
